import { useHistory } from '@packages/router-dom';
import { useCallbackRef } from '../../hooks/useCallbackRef';
import { getHrefPathName } from '../../utils/url';
import { hasNextPageAnchorScroll, resetAnchorScrollPosition } from '../PageAnchorScroll/utils';
import { isBadOrEmptyString } from '../../predicates/string';

function containPhysicalNavLinks(url: string) {
	const urlLowerCase = url.toLowerCase();

	return (
		urlLowerCase.match(/^(https?):\/\/content\..+/gim) ||
		urlLowerCase.includes('gluedcontent') ||
		['mailto:', 'tel:', 'blob:', 'sms:', 'file:', 'skype:', 'callto:'].some((prefix) =>
			urlLowerCase.startsWith(prefix),
		)
	);
}

function isExternalLink(url: string) {
	if (url.startsWith('/') || url.startsWith('.') || url.startsWith(window.location.origin)) {
		return false;
	}

	return url.match(/^(https?):\/\/(www)?.+/gim);
}

function urlStartsWithActiveOrigin(url: string) {
	return url.startsWith(window.location.origin);
}

function useRouteLinkNavigation() {
	const history = useHistory();

	return useCallbackRef(
		({
			openInNewTab,
			href,
			replace = false,
			scroll = true,
			forceUseLink,
		}: {
			openInNewTab: boolean;
			href?: string | null;
			replace: boolean;
			scroll?: boolean;
			forceUseLink?: boolean;
		}) => {
			if (isBadOrEmptyString(href)) {
				return;
			}

			// Control/Command key was pressed when the click event occurred
			if (openInNewTab) {
				if (scroll) {
					const nextPathName = getHrefPathName(href);

					if (hasNextPageAnchorScroll(nextPathName)) {
						resetAnchorScrollPosition(nextPathName);
					}
				}

				window.open(href, '_blank');

				return;
			}

			if (forceUseLink || isExternalLink(href) || containPhysicalNavLinks(href)) {
				window.location.href = href;

				return;
			}

			// should simplify url to make react router works properly
			if (urlStartsWithActiveOrigin(href)) {
				href = href.replace(window.location.origin, '');
			}

			const navigationFunc = replace ? history.replace : history.push;

			if (href) {
				navigationFunc(
					href,
					{
						prevUrl: history.location.pathname,
					},
					{
						scroll,
					},
				);
			}
		},
	);
}

export { useRouteLinkNavigation };
